
import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";
import { useLanguage } from '@/contexts/LanguageContext';
import { socialApi } from '@/api/social';
import { toast } from 'sonner';
import { DerivativeCreation } from '@/data/models/types';
import { VideoMasonryGrid } from '@/components/derivatives/VideoMasonryGrid';

const FavoritesManagement = () => {
  const { t } = useLanguage();
  const [favorites, setFavorites] = useState<DerivativeCreation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await socialApi.getUserFavorites();
      
      if (error) {
        console.error('Error fetching favorites:', error);
        setError(typeof error === 'string' ? error : t('favorites.errorLoading'));
        toast.error(t('favorites.errorLoading'));
        return;
      }
      
      // Debug the raw data from API to ensure social metrics are present
      console.log('Raw favorites data from API:', data);
      
      // Transform data to match DerivativeCreation format for VideoMasonryGrid
      // With explicit mapping of all social metrics fields
      const formattedFavorites: DerivativeCreation[] = data.map(item => ({
        id: item.id,
        title: item.title,
        thumbnail: item.thumbnail || item.thumbnail_url,
        status: item.status,
        expressionType: item.expression_type,
        submittedAt: item.submitted_at || item.submitted_date,
        approvedAt: item.approved_at || item.approved_date,
        rejectedAt: item.rejected_at || item.rejected_date,
        reviewComment: item.rejection_reason || item.review_comment,
        clipId: item.clip_id || "",
        creatorId: item.creator_id || "",
        creatorName: item.creator_name || "Unknown Creator",
        description: item.description || "",
        videoUrl: item.video_url || "",
        aspectRatio: Number(item.aspect_ratio) || 16/9,
        
        // Explicitly map all social metrics to ensure they're properly passed
        // Make sure they are never undefined and map from both snake_case and camelCase properties
        likesCount: Math.max(0, Number(item.likes_count || item.likesCount || 0)),
        commentsCount: Math.max(0, Number(item.comments_count || item.commentsCount || 0)),
        favoritesCount: Math.max(0, Number(item.favorites_count || item.favoritesCount || 0)),
        views: Math.max(0, Number(item.views || 0))
      }));
      
      // Debug the formatted items to ensure social metrics are mapped correctly
      console.log('Formatted favorites with social metrics:', formattedFavorites);
      
      setFavorites(formattedFavorites);
      setError(null);
    } catch (err) {
      console.error('Unexpected error in fetchFavorites:', err);
      setError(t('common.errorOccurred'));
      toast.error(t('favorites.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFromFavorites = async (id: string) => {
    try {
      const result = await socialApi.unfavoriteDerivative(id);
      if (result.success) {
        toast.success(t('favorites.removedSuccess'));
        // Remove from local state
        setFavorites(prev => prev.filter(item => item.id !== id));
      } else {
        toast.error(t('favorites.removeFailed'));
      }
    } catch (err) {
      console.error('Error removing favorite:', err);
      toast.error(t('common.errorOccurred'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('common.error')}</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">{t('favorites.noFavorites')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <VideoMasonryGrid
        items={favorites}
        isLoading={isLoading}
        error={error}
        forceVerticalLayout={true}
      />
    </div>
  );
};

export default FavoritesManagement;
